import { useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Avatar, Button, Callout, Field, Skeleton } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { useMe, useSetMe, type Me } from '../lib/session.ts';
import { AuthCanvas } from './Login.tsx';

/** Compatibility page for apps still using the legacy /oauth2 flow. */
export function LegacyAuthorize() {
  const search = useSearch({ strict: false }) as { client_id: string; state?: string; mode: 'code' | 'token'; error?: string };
  const me = useMe();
  const setMe = useSetMe();
  const client = useQuery({ queryKey: ['legacy-client', search.client_id], queryFn: () => get<{ name: string; host: string | null }>(`/api/legacy/client/${encodeURIComponent(search.client_id)}`), retry: false });
  const [other, setOther] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(search.error === 'credentials' ? 'That email and password don’t match.' : null);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    const r = await post<{ redirectTo: string }>('/api/legacy/approve', { client_id: search.client_id, state: search.state, mode: search.mode });
    window.location.assign(r.redirectTo);
  };

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const m = await post<Me>('/api/auth/login', { email, password });
      setMe(m);
      await approve();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  const name = client.data?.name ?? 'this app';
  return (
    <AuthCanvas statement={<>Continue to <em>{name}</em>.</>}>
      <div className="auth__form-inner">
        {client.isLoading || me.isLoading ? (
          <Skeleton h={120} />
        ) : client.isError ? (
          <Callout tone="bad" title="Unknown application.">
            The app that sent you here isn’t registered for sign-in. Ask the platform team to add it.
          </Callout>
        ) : me.data && !other ? (
          <div className="stack">
            <span className="eyebrow">Sign in to {name}</span>
            <h1 className="display" style={{ fontSize: 'var(--step-4)' }}>
              Continue as <em>{me.data.displayName.split(' ')[0]}?</em>
            </h1>
            <div className="me-card">
              <Avatar name={me.data.displayName} />
              <div>
                <div className="me-card__name">{me.data.displayName}</div>
                <div className="me-card__role">{me.data.email}</div>
              </div>
            </div>
            {error ? <Callout tone="bad">{error}</Callout> : null}
            <Button
              variant="primary"
              size="lg"
              loading={busy}
              iconRight="arrowRight"
              onClick={async () => {
                setBusy(true);
                try {
                  await approve();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Something went wrong.');
                  setBusy(false);
                }
              }}
            >
              Continue
            </Button>
            <Button variant="ghost" onClick={() => setOther(true)}>
              Use another account
            </Button>
          </div>
        ) : (
          <form className="stack" onSubmit={signIn}>
            <span className="eyebrow">Sign in to {name}</span>
            <h1 className="display" style={{ fontSize: 'var(--step-4)' }}>
              Welcome <em>back.</em>
            </h1>
            {error ? <Callout tone="bad">{error}</Callout> : null}
            <Field label="Work email">{(p) => <input {...p} className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Field label="Password">{(p) => <input {...p} className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
            <Button variant="primary" size="lg" type="submit" loading={busy} iconRight="arrowRight">
              Sign in
            </Button>
          </form>
        )}
      </div>
    </AuthCanvas>
  );
}
